// Szolgáltatás-hozzárendelés űrlap tiszta segédei (nem-komponens): a form-értékek típusa, a betöltött detail →
// form-értékek leképezés, a KATALÓGUS-plan → ár-előtöltés, a form → wire-body építők (create teljes / update
// PARTIAL a szerkesztés-lock szerint), a szerkeszthető-mező kalkulátor (számlázott assignment lock), a resolver-
// séma-gyár és a lokalizált zod-hibatérkép. Külön fájl a komponenstől (Fast Refresh). A minta a features/invoices.
//
// Phase-2: ÁR-OVERRIDE (a Phase-1 katalógus-árazó blokk újrahasznosítva a `@/lib/pricing-form`-ból), payment-card,
// contact/expert pivot, copy-to-accounts (client-scope), invoiceItem* + showComponentsOnInvoice. HALASZTOTT: a
// komponens-fa (parentId — mindig root); usage-feed tényleges jutalék.
// A DÁTUMOK ISO date-stringként (YYYY-MM-DD) mennek — a `@db.Date` határ NE csússzon UTC-időre; a pénz DECIMAL-
// string (soha float).
import {
  type ServiceAssignmentCreateBodySchema,
  type ServiceAssignmentDetailSchema,
  type ServiceAssignmentUpdateBodySchema,
} from '@atlas/contracts';
import {
  INVOICE_DELIVERY_TYPES,
  INVOICE_ISSUER_KEYS,
  INVOICE_QUANTITY_UNITS,
  INVOICE_TYPES,
  type InvoiceDeliveryType,
  type InvoiceIssuerKey,
  type InvoiceQuantityUnit,
  type InvoiceType,
  parseDecimal2,
  bindableIntegrations,
  canSplitSharedUsage,
  type AccountIntegration,
} from '@atlas/shared';
import { type TFunction } from 'i18next';
import { type z } from 'zod';
import { z as zod } from 'zod';

import {
  allowedPricingTypes,
  buildPriceRows,
  CURRENCIES,
  type Currency,
  PRICING_TYPES,
  type PricingFormValues,
  type PricingType,
  pricesToForm,
  type RawPriceRow,
  validatePricingForm,
  VAT_RATES,
  type VatRate,
} from '@/lib/pricing-form';

type ServiceAssignmentDetail = z.infer<typeof ServiceAssignmentDetailSchema>;
type CreateBody = z.input<typeof ServiceAssignmentCreateBodySchema>;
type UpdateBody = z.input<typeof ServiceAssignmentUpdateBodySchema>;

// ── UI-opciólisták (a contract enumjaival érték-azonosak; a body-validálás a @atlas/contracts-ból jön) ──
export const SERVICE_ASSIGNMENT_SCOPES = ['client', 'account'] as const;
export const SERVICE_ASSIGNMENT_TYPES = ['tracking', 'billing'] as const;
export const SERVICE_ASSIGNMENT_STATUSES = ['active', 'pending', 'inactive'] as const;
export const SERVICE_ASSIGNMENT_BILLING_FREQUENCIES = [
  'monthly',
  'quarterly',
  'semi_annual',
  'annual',
] as const;
export const SERVICE_ASSIGNMENT_BILLING_AGREEMENTS = ['prepaid', 'postpaid'] as const;
/** Használat-forrás értékkészlet (a contract UsageSourceTypeSchema 1:1) — a form-választó + a label-forrás. */
export const SERVICE_ASSIGNMENT_USAGE_SOURCE_TYPES = [
  'newsletter',
  'loyalty_newsletter',
  'rs_loyalty',
  'rs_direct_referral',
  'rs_direct',
  'rs_referral',
  'rs_voucher',
  'ticket',
] as const;
/** Az az árazás-típus-halmaz, amelynél a usageSourceType KÖTELEZŐ (a BFF is 422-zi). */
export const USAGE_SOURCE_REQUIRED_PRICING_TYPES = ['tiered', 'base_tiered', 'commission'] as const;

/** A usageSourceType mező LÁTHATÓ/kötelező-e az adott (effektív) árazás-típusnál (tiered/base_tiered/commission). */
export function usageSourceRequired(pricingType: string): boolean {
  return (USAGE_SOURCE_REQUIRED_PRICING_TYPES as readonly string[]).includes(pricingType);
}

export type ServiceScope = (typeof SERVICE_ASSIGNMENT_SCOPES)[number];
export type ServiceType = (typeof SERVICE_ASSIGNMENT_TYPES)[number];
export type ServiceStatus = (typeof SERVICE_ASSIGNMENT_STATUSES)[number];
export type ServiceBillingFrequency = (typeof SERVICE_ASSIGNMENT_BILLING_FREQUENCIES)[number] | '';
export type ServiceBillingAgreement = (typeof SERVICE_ASSIGNMENT_BILLING_AGREEMENTS)[number] | '';

/** A hozzárendelés-ár-override form-alakja: a KÖZÖS árazó-blokk (pricingType/primaryPrice/tiers/overflow) + a
 *  hozzárendelés-szintű `vatRate` (a snapshot ÁFA-felülírása). */
export interface AssignmentPricingFormValues extends PricingFormValues {
  vatRate: VatRate;
}

/**
 * Egy KOMPONENS-fa sor form-alakja (komplex/csomag hozzárendelés gyerek-komponense — a legacy formComponents[] 1:1).
 * A `serviceId` UI-only (a plan-lista szűrője, NEM megy a wire-be). Az `id` a MEGLÉVŐ gyerek azonosítója (edit-reconcile);
 * új komponensnél ''. A gyerek mindig fix (unit) áras — a `unitPrice`/`currency` a `service-plan-assignment-prices` sora.
 */
export interface ServiceAssignmentComponentValue {
  id: string;
  serviceId: string;
  servicePlanId: string;
  quantity: string;
  unitPrice: string;
  currency: Currency;
  /** KOMPONENS-SZINTŰ felelősök (pivot a gyerek soron) — a legacy `formComponentContactIds[i]` 1:1. */
  contactId: string;
  expertId: string;
}

export interface ServiceAssignmentFormValues {
  scope: ServiceScope;
  clientId: string;
  /** account-scope: a fiók id-ja; client-scope-nál ''. */
  accountId: string;
  /** UI-only: a szolgáltatás id-ja (a plan-lista szűrője) — NEM megy a wire-be. */
  serviceId: string;
  servicePlanId: string;
  type: ServiceType;
  status: ServiceStatus;
  quantity: string;
  quantityUnit: InvoiceQuantityUnit;
  issuerKey: InvoiceIssuerKey;
  invoiceType: InvoiceType;
  invoiceDeliveryType: InvoiceDeliveryType;
  billingFrequency: ServiceBillingFrequency;
  billingAgreement: ServiceBillingAgreement;
  billingStartDate: string;
  billingEndDate: string;
  contractStartDate: string;
  contractEndDate: string;
  firstInvoiceProrated: boolean;
  note: string;
  // ── Phase-2 mezők ──
  /** Ár-override (a katalógus-árból előtöltve; billing típusnál szerkeszthető). */
  pricing: AssignmentPricingFormValues;
  /** UI-only: a KATALÓGUS-plan pricingType-ja — a down-conversion korlát forrása (NEM megy a wire-be). */
  catalogPricingType: string;
  /**
   * A KATALÓGUS-plan díjtípusa (`recurring` · `one_time` · '' = még nincs plan). Az ESETI díj (`one_time`)
   * EGYETLEN dátumot kap — a „Számlázás dátuma" a `billingStartDate`, a vég vele azonos, szerződés-dátum nincs
   * (user-döntés); a sor `type`-ja ezt nem árulja el (az ismétlődő nyilvántartás is `tracking`).
   */
  billingType: string;
  /** A kliens bankkártyája (payment-cards); '' → nincs. */
  paymentCardId: string;
  /** Kapcsolattartó / szakértő identity (pivot role=contact|expert); '' → nincs. */
  contactId: string;
  expertId: string;
  /** Client-scope: több érintett fiók (assignment-accounts pivot). */
  accountIds: string[];
  /** Egyedi számlatétel-név/megjegyzés (a snapshot felülírása; '' → a katalógus-érték marad). */
  invoiceItemName: string;
  invoiceItemNote: string;
  /** Komponensek megjelenítése a számlatétel-megjegyzésben. */
  showComponentsOnInvoice: boolean;
  /** Használat-forrás (a snapshot usageSourceType felülírása; '' → nincs). Tiered/base_tiered/commission
   *  árazásnál KÖTELEZŐ (a UI + a BFF is validálja). */
  usageSourceType: string;
  /**
   * A MÉRT AZONOSÍTÓK: a kiválasztott fiók-azonosítók `accountId|integrationId` kulcsai (l. `usageRefKey`). Használat-
   * alapú tételnél KÖTELEZŐ, legalább egy — ezek mérése adja a tétel összegét.
   */
  usageRefs: string[];
  /**
   * MEGOSZTOTT HASZNÁLAT: a regisztráltak utáni díjrész az ugyanarra az azonosítóra kötött, bekapcsolt társak között
   * oszlik. CSAK `rs_loyalty` forrásnál kapcsolható (l. `canSplitSharedUsage`); máshol mindig `false` megy.
   */
  splitSharedUsage: boolean;
  /** KOMPONENS-FA (komplex hozzárendelés gyerekei). Nem-üres → a szülő árazása flat, ára = a komponensek összege. */
  components: ServiceAssignmentComponentValue[];
}

/** Üres ár-override (create-default): fix díj, HUF, ÁFA 27%, üres primary-ár. */
export function emptyAssignmentPricing(): AssignmentPricingFormValues {
  return {
    pricingType: 'flat',
    vatRate: 'VAT_27',
    primaryPrice: '',
    primaryCurrency: 'HUF',
    tiers: [],
    overflowEnabled: false,
    overflowBlockSize: '',
    overflowPrice: '',
    overflowCurrency: 'HUF',
  };
}

/** A mai nap ISO date-only (YYYY-MM-DD), HELYI idő szerint (a `@db.Date` határ NEM csúszhat UTC-re). */
function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Üres form (create-mód default) — a legacy 1:1: számlázandó (billing), fiók-scope, aktív, 1 db, számlázás-kezdet
 * = MA, utólagos (postpaid) megállapodás. (A tulajdonos-státusz a form-sheetben szűkítheti a státuszt.)
 */
export const EMPTY_SERVICE_ASSIGNMENT_FORM: ServiceAssignmentFormValues = {
  scope: 'account',
  clientId: '',
  accountId: '',
  serviceId: '',
  servicePlanId: '',
  type: 'billing',
  status: 'active',
  quantity: '1',
  quantityUnit: 'piece',
  issuerKey: 'none',
  invoiceType: 'invoice',
  invoiceDeliveryType: 'electronic',
  billingFrequency: '',
  billingAgreement: 'postpaid',
  billingStartDate: todayIsoDate(),
  billingEndDate: '',
  contractStartDate: '',
  contractEndDate: '',
  firstInvoiceProrated: false,
  note: '',
  pricing: emptyAssignmentPricing(),
  catalogPricingType: '',
  billingType: '',
  paymentCardId: '',
  contactId: '',
  expertId: '',
  accountIds: [],
  invoiceItemName: '',
  invoiceItemNote: '',
  showComponentsOnInvoice: false,
  usageSourceType: '',
  usageRefs: [],
  splitSharedUsage: false,
  components: [],
};

/** Egy üres komponens-sor (a „komponens hozzáadása" gomb default értéke). */
export function emptyComponent(): ServiceAssignmentComponentValue {
  return {
    id: '',
    serviceId: '',
    servicePlanId: '',
    quantity: '1',
    unitPrice: '',
    currency: 'HUF',
    contactId: '',
    expertId: '',
  };
}

/**
 * A komponens-választó billing-type-szűrő CÉL-típusa a FŐ plan billing-type-jából (a legacy component-modal 1:1):
 * a fő plan `one_time` → a komponensek is CSAK `one_time`-osak lehetnek; minden más eset (recurring VAGY még nincs
 * kiválasztott fő plan) → `recurring`. A legacy binárisan kezeli (`isMainOneTime ? one_time : recurring`).
 */
export function componentTargetBillingType(
  mainBillingType: string | null | undefined,
): 'one_time' | 'recurring' {
  return mainBillingType === 'one_time' ? 'one_time' : 'recurring';
}

/**
 * Egy szolgáltatás választható-e KOMPONENSNEK a fő billing-type mellett: van-e a `targetBillingType`-pal EGYEZŐ
 * katalógus-planje. A legacy component-modal service-szűrőjének 1:1 tükre — a simple/package megkülönböztetést az
 * Atlas plan-központú modellje feloldja (a plan hordozza a `billingType`-ot, így az „van-e egyező plan" mindkét
 * struktúra-típusra egységesen helyes). Üres `serviceId` → false (nincs kiválasztva).
 */
export function serviceMatchesComponentBillingType(
  serviceId: string,
  plans: readonly { serviceId: string; billingType: string | null }[],
  targetBillingType: string,
): boolean {
  if (serviceId === '') return false;
  return plans.some((p) => p.serviceId === serviceId && p.billingType === targetBillingType);
}

/**
 * A FŐ CSOMAG MINT KOMPONENS (user-döntés, a legacy CRM működése szerint): komponens módban a szülő ára a komponensek
 * összege — az alapdíj tehát CSAK úgy marad meg, ha maga a fő csomag is komponens. Az első „Komponens hozzáadása"
 * ezért a fő csomagot AUTOMATIKUSAN első komponensnek teszi, a már beírt árral (fix árnál az egységár; más árazásnál
 * üresen — azt a felhasználó adja meg). Így az alapdíj sosem veszhet el csendben.
 */
export function mainPlanComponent(v: {
  serviceId: string;
  servicePlanId: string;
  quantity: string;
  pricing: { pricingType: string; primaryPrice: string; primaryCurrency: Currency };
}): ServiceAssignmentComponentValue {
  return {
    ...emptyComponent(),
    serviceId: v.serviceId,
    servicePlanId: v.servicePlanId,
    quantity: v.quantity.trim() === '' ? '1' : v.quantity.trim(),
    unitPrice: v.pricing.pricingType === 'flat' ? v.pricing.primaryPrice.trim() : '',
    currency: v.pricing.primaryCurrency,
  };
}

/**
 * Ha a komponens-listában CSAK a fő csomag maradt (minden más komponenst töröltek), a sor visszaállhat EGYSZERŰ
 * szolgáltatássá azzal az árral és mennyiséggel — különben egy egy-komponenses „komplex" sor maradna. `null` → nincs
 * mit visszaállítani.
 */
export function collapseToMainPlan(
  components: readonly ServiceAssignmentComponentValue[],
  servicePlanId: string,
): { unitPrice: string; currency: Currency; quantity: string } | null {
  if (components.length !== 1) return null;
  const only = components[0]!;
  if (servicePlanId === '' || only.servicePlanId !== servicePlanId) return null;
  return { unitPrice: only.unitPrice, currency: only.currency, quantity: only.quantity };
}

/**
 * A komponens-csomagok NEVEI vesszővel — a „komponensek a számlatételen" kapcsoló generált megjegyzése (a legacy
 * `updateInvoiceNoteFromComponents` 1:1, pl. „Marketing Alap, SEO Audit"). A még ki nem választott (üres) és az
 * ismeretlen plan-ú sorok kimaradnak; nincs kiválasztott komponens → üres string.
 */
export function componentPlanNames(
  components: readonly { servicePlanId: string }[],
  plans: readonly { id: string; name: string }[],
): string {
  return components
    .map((c) => plans.find((p) => p.id === c.servicePlanId)?.name ?? '')
    .filter((n) => n !== '')
    .join(', ');
}

/**
 * A komponensek ÖSSZEGE (a szülő számított alap-ára) FIXPONTOSAN (Decimal-string, SOHA float) + a közös deviza (null,
 * ha a komponensek devizája nem egységes). Üres lista → { amount: '0', currency: null }. Ez a UI kijelző-forrása; a
 * tényleges szülő-árat a BFF is Σ-ként számolja (a kézi ár tilos komplexnél).
 */
export function componentsTotal(components: ServiceAssignmentComponentValue[]): {
  amount: string;
  currency: string | null;
} {
  if (components.length === 0) return { amount: '0', currency: null };
  const currencies = new Set(components.map((c) => c.currency));
  const currency = currencies.size === 1 ? [...currencies][0]! : null;
  try {
    let scaled = 0n; // ×100 skálán
    for (const c of components) {
      scaled += (parseDecimal2(c.unitPrice) * parseDecimal2(c.quantity) + 50n) / 100n;
    }
    const amount =
      scaled % 100n === 0n
        ? (scaled / 100n).toString()
        : `${scaled / 100n}.${(scaled % 100n).toString().padStart(2, '0')}`;
    return { amount, currency };
  } catch {
    return { amount: '0', currency };
  }
}

/**
 * A KAPCSOLATTARTÓ-választó fiók-halmaza (a legacy `loadAvailableContacts` 1:1):
 *   • fiók-hatókör + kiválasztott fiók → CSAK az a fiók,
 *   • ügyfél-hatókör + kijelölt érintett fiókok → a KIJELÖLT fiókok,
 *   • ügyfél-hatókör kijelölés nélkül → az ügyfél összes fiókja,
 *   • egyébként (nincs még tulajdonos) → üres.
 *
 * MIÉRT A KIJELÖLT FIÓKOK, nem az ügyfél összes fiókja: egy csoportos szerződés gyakran az ügyfél fiókjainak
 * CSAK EGY RÉSZÉRE szól (élesben: 27 fiókos ügyfélnél is), és a kapcsolattartó annak a hotelnek az embere.
 * A teljes ügyfél-névsorból bármikor a szerződésen kívüli hotel kapcsolattartója választódna — és ezt semmi
 * nem jelezné, mert a pivot adat-szinten bármelyik személyt elfogadja.
 */
export function contactAccountScope(
  scope: ServiceScope,
  accountId: string,
  accountIds: readonly string[],
  clientAccountIds: readonly string[],
): string[] {
  if (scope === 'account') return accountId === '' ? [] : [accountId];
  const kijelolt = accountIds.filter((id) => id !== '');
  return kijelolt.length > 0 ? kijelolt : [...clientAccountIds];
}

// ── A MÉRT AZONOSÍTÓK kötése ─────────────────────────────────────────────────────────────────────────────
//
// Egy fióknak rendszerenként több külső azonosítója lehet (a HotelCMS-ben az egység egy domain — külön loyalty-,
// booking-, ticket-domain), ezért a használat-alapú tétel KIFEJEZETTEN megmondja, melyik fiók melyik azonosítójának
// mérése adja az összegét. A form a párokat egyetlen string-kulcsként tartja (a több-választó egy kulccsal dolgozik).

/** Egy kötés form-kulcsa: `accountId|integrationId`. */
export function usageRefKey(ref: { accountId: string; integrationId: string }): string {
  return `${ref.accountId}|${ref.integrationId}`;
}

/** A form-kulcs → a wire-pár. */
export function parseUsageRefKey(key: string): { accountId: string; integrationId: string } {
  const [accountId = '', integrationId = ''] = key.split('|');
  return { accountId, integrationId };
}

/**
 * Használat-alapú-e a tétel a form szerint: sávos / alapdíj+sávos / jutalékos árazás, választott forrással, és
 * nem komplex (a komplex szülő ára a komponensek összege). A BFF ugyanígy dönt (`isUsageBased`).
 */
export function isUsageBasedForm(values: {
  pricing: { pricingType: string };
  usageSourceType: string;
  components: readonly unknown[];
}): boolean {
  return (
    values.components.length === 0 &&
    usageSourceRequired(values.pricing.pricingType) &&
    values.usageSourceType.trim() !== ''
  );
}

/** Egy választható mért azonosító (a több-választó egy opciója). */
export interface UsageRefOption {
  key: string;
  accountId: string;
  accountName: string;
  integration: AccountIntegration;
}

/**
 * A választható mért azonosítók: fiók-szintű tételnél a fiók azonosítói, ügyfél-szintűnél a kijelölt ÉRINTETT
 * FIÓKOKÉ — mindkét esetben CSAK a köthetők (`bindableIntegrations`: hírlevél → MailWizz · jutalék → HotelCMS · a
 * többi → HotelCMS vagy ZadirCMS). Sorrend: fiók-név, azon belül a fiók-lap sorrendje.
 */
export function usageRefOptions(args: {
  scope: string;
  accountId: string;
  accountIds: readonly string[];
  usageSourceType: string;
  accounts: readonly { id: string; name: string; integrations: readonly AccountIntegration[] }[];
}): UsageRefOption[] {
  if (args.usageSourceType.trim() === '') return [];
  const ids = args.scope === 'account' ? [args.accountId] : args.accountIds;
  const out: UsageRefOption[] = [];
  for (const account of args.accounts
    .filter((a) => ids.includes(a.id))
    .sort((a, b) => a.name.localeCompare(b.name, 'hu'))) {
    // a BFF-fel AZONOS szabály: a forráshoz illő rendszer, és a HotelCMS-párú fiók ZadirCMS-azonosítója nem köthető
    for (const integration of bindableIntegrations(account.integrations, args.usageSourceType)) {
      out.push({
        key: usageRefKey({ accountId: account.id, integrationId: integration.id }),
        accountId: account.id,
        accountName: account.name,
        integration,
      });
    }
  }
  return out;
}

const asType = <T extends string>(values: readonly T[], v: string | null, fallback: T): T =>
  values.includes(v as T) ? (v as T) : fallback;

/** Nullable enum → az érték, ha a listában van, különben '' (a „nincs megadva" opció a UI-ban). */
const asOptional = <T extends string>(values: readonly T[], v: string | null): T | '' =>
  values.includes(v as T) ? (v as T) : '';

/**
 * Az ÁR-OVERRIDE választható pricingType-jai a katalógus-plan típusából (down-conversion; a `@/lib/pricing-form`
 * `allowedPricingTypes`-ja): katalógus `flat`/üres → CSAK `flat`; egyébként {flat, katalógus-típus}.
 */
export function allowedAssignmentPricingTypes(catalogPricingType: string): PricingType[] {
  return allowedPricingTypes(catalogPricingType);
}

/**
 * A tulajdonos (ügyfél/fiók) státusza szerint MEGENGEDETT hozzárendelés-státuszok (CSAK create; a legacy
 * `getAllowedAssignmentStatuses` 1:1, a BFF `allowedAssignmentStatuses`-ának tükre): active/ismeretlen/null →
 * mind; pending → CSAK pending; inactive → pending/inactive. Az első elem a „legmagasabb" engedélyezett (az
 * auto-választás forrása, a legacy `allowedStatuses[0]` szerint).
 */
export function allowedStatuses(ownerStatus: string | null): ServiceStatus[] {
  if (ownerStatus === 'pending') return ['pending'];
  if (ownerStatus === 'inactive') return ['pending', 'inactive'];
  return ['active', 'pending', 'inactive'];
}

/**
 * A KATALÓGUS-plan (refs) → ár-override form-előtöltés (create-mód, plan-választáskor). A pricingType/vatRate a
 * plan-sorból, az ár-sorok a plan-árakból (`pricesToForm`); a `catalogPricingType` a down-conversion korlát.
 */
export function catalogPricingToForm(
  plan: { pricingType: string | null; vatRate: string | null },
  planPrices: RawPriceRow[],
): { pricing: AssignmentPricingFormValues; catalogPricingType: string } {
  const pricingType = asType(PRICING_TYPES, plan.pricingType, 'flat');
  return {
    pricing: {
      pricingType,
      vatRate: asType(VAT_RATES, plan.vatRate, 'VAT_27'),
      ...pricesToForm(planPrices, pricingType),
    },
    catalogPricingType: plan.pricingType ?? '',
  };
}

/**
 * A betöltött detail → form-értékek (edit-mód). A `serviceId`-t a hívó a plan-katalógusból oldja fel. A KOMPONENS-fa
 * gyerekei a `detail.children`-ből töltődnek; a gyerek `serviceId`-jét a `planServiceMap`-pel oldjuk fel (a plan-lista
 * szűrőjéhez), ha adott (a detail child.serviceId-je jelenleg null).
 */
export function serviceAssignmentToFormValues(
  detail: ServiceAssignmentDetail,
  serviceId: string,
  planServiceMap?: (planId: string) => string,
): ServiceAssignmentFormValues {
  // A snapshot pricingType-ja (az override), fallback a katalógus-típusra, végül flat.
  const pricingType = asType(
    PRICING_TYPES,
    detail.pricingType ?? detail.catalogPricingType,
    'flat',
  );
  return {
    scope: asType(SERVICE_ASSIGNMENT_SCOPES, detail.scope, 'client'),
    clientId: detail.clientId,
    accountId: detail.accountId ?? '',
    serviceId,
    servicePlanId: detail.servicePlanId,
    type: asType(SERVICE_ASSIGNMENT_TYPES, detail.type, 'tracking'),
    status: asType(SERVICE_ASSIGNMENT_STATUSES, detail.status, 'active'),
    quantity: detail.quantity,
    quantityUnit: asType(INVOICE_QUANTITY_UNITS, detail.quantityUnit, 'piece'),
    issuerKey: asType(INVOICE_ISSUER_KEYS, detail.issuerKey, 'none'),
    invoiceType: asType(INVOICE_TYPES, detail.invoiceType, 'invoice'),
    invoiceDeliveryType: asType(INVOICE_DELIVERY_TYPES, detail.invoiceDeliveryType, 'electronic'),
    billingFrequency: asOptional(SERVICE_ASSIGNMENT_BILLING_FREQUENCIES, detail.billingFrequency),
    billingAgreement: asOptional(SERVICE_ASSIGNMENT_BILLING_AGREEMENTS, detail.billingAgreement),
    billingStartDate: detail.billingStartDate ?? '',
    billingEndDate: detail.billingEndDate ?? '',
    contractStartDate: detail.contractStartDate ?? '',
    contractEndDate: detail.contractEndDate ?? '',
    firstInvoiceProrated: detail.firstInvoiceProrated,
    note: detail.note ?? '',
    pricing: {
      pricingType,
      vatRate: asType(VAT_RATES, detail.vatRate, 'VAT_27'),
      ...pricesToForm(detail.prices, pricingType),
    },
    catalogPricingType: detail.catalogPricingType ?? '',
    billingType: detail.billingType ?? '',
    paymentCardId: detail.paymentCardId ?? '',
    contactId: detail.contactId ?? '',
    expertId: detail.expertId ?? '',
    accountIds: detail.accountIds,
    invoiceItemName: detail.invoiceItemName ?? '',
    invoiceItemNote: detail.invoiceItemNote ?? '',
    showComponentsOnInvoice: detail.showComponentsOnInvoice,
    usageSourceType: detail.usageSourceType ?? '',
    // null = a még nem kötött (régi) sor → üres választás; a mentés a kötést kötelezően kéri
    usageRefs: (detail.usageRefs ?? []).map(usageRefKey),
    // `?? false`: egy régebbi BFF válaszából (telepítési ablak) a mező hiányzik — `undefined` mellett az űrlap
    // sémája elbukna, és a mentés NÉMÁN nem indulna el (mérve a form-sheet tesztjén)
    splitSharedUsage: detail.splitSharedUsage ?? false,
    components: detail.children.map((ch) => ({
      id: ch.id,
      contactId: ch.contactId ?? '',
      expertId: ch.expertId ?? '',
      serviceId: ch.serviceId ?? planServiceMap?.(ch.servicePlanId) ?? '',
      servicePlanId: ch.servicePlanId,
      quantity: ch.quantity,
      unitPrice: ch.unitPrice ?? '',
      // A busz deviza-enum-kényszerű; a detail null → HUF fallback. A típust a Currency-unióra szűkítjük.
      currency: (CURRENCIES as readonly string[]).includes(ch.currency ?? '')
        ? (ch.currency as Currency)
        : 'HUF',
    })),
  };
}

const nullableStr = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** Az ár-override wire-blokkja (billing típusnál): pricingType + vatRate + a mátrix szerinti ár-sorok. */
function pricingToWire(pricing: AssignmentPricingFormValues): NonNullable<CreateBody['pricing']> {
  return {
    pricingType: pricing.pricingType,
    vatRate: pricing.vatRate,
    prices: buildPriceRows(pricing),
  };
}

/** A komponens-fa wire-alakja: az `id` CSAK meglévő gyereknél megy (edit-reconcile); a mennyiség üres → '1'. */
function componentsToWire(
  components: ServiceAssignmentComponentValue[],
): NonNullable<CreateBody['components']> {
  return components.map((c) => ({
    ...(c.id.trim() !== '' ? { id: c.id.trim() } : {}),
    servicePlanId: c.servicePlanId,
    quantity: c.quantity.trim() === '' ? '1' : c.quantity.trim(),
    unitPrice: c.unitPrice.trim(),
    currency: c.currency,
    // A pivot-mezőket MINDIG küldjük (üres → `null` = a felelős törlése). A szerver az `undefined`-et
    // „ne bántsd" jelentéssel kezeli, tehát a kulcs kihagyása nem tudná törölni a felelőst.
    contactId: nullableStr(c.contactId),
    expertId: nullableStr(c.expertId),
  }));
}

/** Create-body: a törzs-mezők + a KATALÓGUS-plan azonosítója + az ár-override/pivotok/számla-mezők. */
export function serviceAssignmentFormToCreateBody(values: ServiceAssignmentFormValues): CreateBody {
  const isBilling = values.type === 'billing';
  const isOneTime = values.billingType === 'one_time';
  const isClient = values.scope === 'client';
  return {
    clientId: values.clientId,
    accountId: isClient ? null : values.accountId,
    servicePlanId: values.servicePlanId,
    scope: values.scope,
    type: values.type,
    status: values.status,
    quantity: values.quantity.trim(),
    quantityUnit: values.quantityUnit,
    issuerKey: isBilling ? values.issuerKey : null,
    invoiceType: values.invoiceType,
    // díjbekérő csak papír alapú lehet (a választó is rejtve van) — a szerver is erre kényszerít
    invoiceDeliveryType: values.invoiceType === 'proforma' ? 'paper' : values.invoiceDeliveryType,
    billingFrequency: isBilling && values.billingFrequency !== '' ? values.billingFrequency : null,
    billingAgreement: isBilling && values.billingAgreement !== '' ? values.billingAgreement : null,
    // ESETI DÍJ: egyetlen dátum — a „Számlázás dátuma" a kezdet ÉS a vég; szerződés-dátum nincs. Minden más
    // soron a szerződés-dátum is megy (tájékoztató adat — a számlázást a számlázási pár vezérli).
    billingStartDate: isBilling || isOneTime ? values.billingStartDate : '',
    billingEndDate: isOneTime ? values.billingStartDate : isBilling ? values.billingEndDate : '',
    contractStartDate: isOneTime ? '' : values.contractStartDate,
    contractEndDate: isOneTime ? '' : values.contractEndDate,
    firstInvoiceProrated: isBilling ? values.firstInvoiceProrated : false,
    showComponentsOnInvoice: values.showComponentsOnInvoice,
    paymentCardId: nullableStr(values.paymentCardId),
    invoiceItemName: nullableStr(values.invoiceItemName),
    invoiceItemNote: nullableStr(values.invoiceItemNote),
    usageSourceType: (values.usageSourceType === ''
      ? null
      : values.usageSourceType) as CreateBody['usageSourceType'],
    contactId: nullableStr(values.contactId),
    expertId: nullableStr(values.expertId),
    // client-scope: érintett fiókok; account-scope-nál a szerver figyelmen kívül hagyja → nem küldjük.
    accountIds: isClient ? values.accountIds : undefined,
    // a mért azonosítók CSAK használat-alapú tételnél mennek (más soron a szerver amúgy is null-t ír)
    usageRefs: isUsageBasedForm(values) ? values.usageRefs.map(parseUsageRefKey) : undefined,
    // a kapcsoló CSAK a regisztráltak utáni loyalty-nál mehet igazként (a szerver máshol 422-zi)
    splitSharedUsage:
      isUsageBasedForm(values) && canSplitSharedUsage(values.usageSourceType)
        ? values.splitSharedUsage
        : false,
    // ÁR-OVERRIDE MINDKÉT TÍPUSNÁL. A nyilvántartás (`tracking`) sornak is LEHET ára — sosem számlázzuk, de
    // rögzíteni kell tudni (user-döntés: a legacy CRM-ben is lehetett, és a kimutatásban látszania kell).
    // A BFF a nyilvántartás-sorra CSAK ezt a kifejezett override-ot írja ki, katalógus-másolatot nem.
    // Komplexnél a szülő árát a komponensekből számolja (a `pricing` figyelmen kívül), de a mezőt küldjük.
    pricing: pricingToWire(values.pricing),
    // KOMPONENS-FA: csak ha van komponens (a szülő ekkor komplex → flat, Σ ár).
    components: values.components.length > 0 ? componentsToWire(values.components) : undefined,
    note: nullableStr(values.note),
  };
}

/** A frissítés-body update-mezői (a contract PARTIAL update-jének szerkesztés-lockolt kulcsai). A contact/expert/
 *  accountIds pivotok NEM itt élnek — azokat a szerver sosem zárolja, ezért mindig szinkronizáljuk. */
export type UpdateField =
  | 'status'
  | 'quantity'
  | 'quantityUnit'
  | 'issuerKey'
  | 'invoiceType'
  | 'invoiceDeliveryType'
  | 'billingFrequency'
  | 'billingAgreement'
  | 'billingStartDate'
  | 'billingEndDate'
  | 'contractStartDate'
  | 'contractEndDate'
  | 'firstInvoiceProrated'
  | 'note'
  | 'showComponentsOnInvoice'
  | 'paymentCardId'
  | 'invoiceItemName'
  | 'invoiceItemNote'
  | 'usageSourceType'
  | 'pricing'
  | 'components';

const ALL_UPDATE_FIELDS: UpdateField[] = [
  'status',
  'quantity',
  'quantityUnit',
  'issuerKey',
  'invoiceType',
  'invoiceDeliveryType',
  'billingFrequency',
  'billingAgreement',
  'billingStartDate',
  'billingEndDate',
  'contractStartDate',
  'contractEndDate',
  'firstInvoiceProrated',
  'note',
  'showComponentsOnInvoice',
  'paymentCardId',
  'invoiceItemName',
  'invoiceItemNote',
  'usageSourceType',
  'pricing',
  'components',
];

/**
 * A szerkeszthető update-mezők halmaza a szerkesztés-lock szerint (a szerver 422-je is ezt kényszeríti — a BFF
 * `EDITABLE_WHEN_INVOICED` + a pricing/invoiceItem* fix zárolás 1:1):
 *  - nem-számlázott → MINDEN update-mező szerkeszthető;
 *  - számlázott + one_time → CSAK `note`;
 *  - számlázott + recurring → status/quantity/quantityUnit/issuerKey/billingEndDate/contract-dátumok/note.
 * A számlázott ág EGYIKÉBEN sem szerkeszthető: pricing, invoiceItemName/Note, showComponentsOnInvoice, paymentCardId.
 */
export function computeEditableFields(detail: {
  invoiced: boolean;
  billingType: string | null;
}): Set<UpdateField> {
  if (!detail.invoiced) return new Set(ALL_UPDATE_FIELDS);
  if (detail.billingType === 'recurring') {
    return new Set<UpdateField>([
      'status',
      'quantity',
      'quantityUnit',
      'issuerKey',
      'billingEndDate',
      'contractStartDate',
      'contractEndDate',
      'note',
    ]);
  }
  // one_time (vagy ismeretlen) → csak a jegyzet.
  return new Set<UpdateField>(['note']);
}

/**
 * Update-body a szerkeszthető halmaz szerint (CSAK a megengedett mezőket küldi — a nem-szerkeszthető mező küldése
 * a BFF-en 422). A dátumok '' → null; a billingFrequency/billingAgreement '' → null; a paymentCardId/invoiceItem*
 * '' → null. Az ÁR-OVERRIDE szerkeszthető esetben megy: számlázás-soron mindig, nyilvántartás-soron ha
 * az árazáshoz hozzányúltak (`options.pricingDirty`). A contact/expert pivot MINDIG szinkronizál
 * (a szerver nem zárolja); az accountIds CSAK client-scope-nál.
 */
export function serviceAssignmentFormToUpdateBody(
  values: ServiceAssignmentFormValues,
  editable: Set<UpdateField>,
  options: { pricingDirty?: boolean } = {},
): UpdateBody {
  const body: UpdateBody = {};
  // a TÍPUS sosem zárolt (számlázott soron is javítható — a jövőbeli generálásra hat); eseti díjnál a szerver
  // nyilvántartásra kényszerít, plan-váltásnál a cél-plan dönt
  body.type = values.type;
  if (editable.has('status')) body.status = values.status;
  if (editable.has('quantity')) body.quantity = values.quantity.trim();
  if (editable.has('quantityUnit')) body.quantityUnit = values.quantityUnit;
  if (editable.has('issuerKey'))
    body.issuerKey = values.type === 'billing' ? values.issuerKey : null;
  if (editable.has('invoiceType')) body.invoiceType = values.invoiceType;
  if (editable.has('invoiceDeliveryType')) {
    body.invoiceDeliveryType =
      values.invoiceType === 'proforma' ? 'paper' : values.invoiceDeliveryType;
  }
  if (editable.has('billingFrequency')) {
    body.billingFrequency = values.billingFrequency === '' ? null : values.billingFrequency;
  }
  if (editable.has('billingAgreement')) {
    body.billingAgreement = values.billingAgreement === '' ? null : values.billingAgreement;
  }
  // ESETI DÍJ: egyetlen dátum (vég := kezdet, szerződés-dátum nincs) — a szerver is erre kényszerít
  const isOneTime = values.billingType === 'one_time';
  if (editable.has('billingStartDate')) body.billingStartDate = values.billingStartDate;
  if (editable.has('billingEndDate')) {
    body.billingEndDate = isOneTime ? values.billingStartDate : values.billingEndDate;
  }
  if (editable.has('contractStartDate')) {
    body.contractStartDate = isOneTime ? '' : values.contractStartDate;
  }
  if (editable.has('contractEndDate')) {
    body.contractEndDate = isOneTime ? '' : values.contractEndDate;
  }
  if (editable.has('firstInvoiceProrated')) body.firstInvoiceProrated = values.firstInvoiceProrated;
  if (editable.has('showComponentsOnInvoice')) {
    body.showComponentsOnInvoice = values.showComponentsOnInvoice;
  }
  if (editable.has('paymentCardId')) body.paymentCardId = nullableStr(values.paymentCardId);
  if (editable.has('invoiceItemName')) body.invoiceItemName = nullableStr(values.invoiceItemName);
  if (editable.has('invoiceItemNote')) body.invoiceItemNote = nullableStr(values.invoiceItemNote);
  if (editable.has('usageSourceType')) {
    body.usageSourceType = (
      values.usageSourceType === '' ? null : values.usageSourceType
    ) as UpdateBody['usageSourceType'];
  }
  // ÁR-OVERRIDE: számlázás-soron mindig; NYILVÁNTARTÁS-soron (pl. az eseti díj — az eseti csomag mindig ilyen)
  // akkor, ha az árazáshoz hozzányúltak. MÉRT HIBA (user-lelet): korábban a nyilvántartás-sor ára SOHA nem ment
  // el szerkesztéskor — az átírt eseti díj nem mentődött, és a listán a régi (0-s) ár maradt, miközben a
  // felvétel mindkét típusnál küldi. Mindig küldeni viszont nem lehet: az ár NÉLKÜLI nyilvántartás-sor minden
  // mentése egy üres ársorral bukna el.
  if (editable.has('pricing') && (values.type === 'billing' || options.pricingDirty === true)) {
    body.pricing = pricingToWire(values.pricing);
  }
  // KOMPONENS-FA reconcile: CSAK ha szerkeszthető (nem-számlázott) ÉS van komponens (a szülő komplex marad). A
  // szerver a fát a kívánt halmazra hozza + a szülő árát Σ-ra. (Az összes komponens eltávolítása → a hozzárendelést
  // törölni kell, nem üres fát küldeni — a `length > 0` védőkapu ezt a ritka, adatvesztő flow-t zárja ki.)
  if (editable.has('components') && values.components.length > 0) {
    body.components = componentsToWire(values.components);
  }
  // PLAN-VÁLTÁS: a servicePlanId CSAK nem-számlázott assignmentnél mehet (a `pricing` editable-je ezt jelzi);
  // a BFF no-op-ol, ha egyezik a jelenlegivel. Számlázottnál a lock miatt nem küldjük (különben 422).
  if (editable.has('pricing')) body.servicePlanId = values.servicePlanId;
  if (editable.has('note')) body.note = nullableStr(values.note);
  // Pivotok (nem lockoltak): mindig szinkronizáljuk. accountIds csak client-scope-nál.
  body.contactId = nullableStr(values.contactId);
  body.expertId = nullableStr(values.expertId);
  if (values.scope === 'client') body.accountIds = values.accountIds;
  // A MÉRT AZONOSÍTÓK kötése sosem zárolt (számlázott soron is javítható — a jövőbeli számlákra hat), ezért
  // használat-alapú tételnél mindig a teljes listát küldjük.
  if (isUsageBasedForm(values)) body.usageRefs = values.usageRefs.map(parseUsageRefKey);
  // a megosztott használat kapcsolója ugyanígy nem zárolt; nem-loyalty forrásnál a szerver amúgy is kikapcsolja
  if (isUsageBasedForm(values) && canSplitSharedUsage(values.usageSourceType)) {
    body.splitSharedUsage = values.splitSharedUsage;
  }
  return body;
}

// ── Resolver-séma-gyár (kliens-oldali elő-ellenőrzés; a szerver a végső fal) ──

// NB: a NEM-árazó check-ek NEM kapnak üzenet-stringet (a lokalizáló hibatérkép fordítja `path`+`code` alapján).
// Az ÁRAZÓ blokk (nested `pricing.*` utak) viszont KÖZVETLEN üzenetet kap (a `validatePricingForm`-ból) — a nested
// tömb-utak miatt az errorMap-alapú fordítás kétértelmű lenne (a katalógus mintája).
const decimalField = zod.string().regex(/^\d+(\.\d{1,2})?$/);

const tierSchema = zod.object({
  tierMin: zod.string(),
  tierMax: zod.string(),
  netPrice: zod.string(),
  currency: zod.enum(CURRENCIES),
});

const pricingSchema = zod.object({
  pricingType: zod.enum(PRICING_TYPES),
  vatRate: zod.enum(VAT_RATES),
  primaryPrice: zod.string(),
  primaryCurrency: zod.enum(CURRENCIES),
  tiers: zod.array(tierSchema),
  overflowEnabled: zod.boolean(),
  overflowBlockSize: zod.string(),
  overflowPrice: zod.string(),
  overflowCurrency: zod.enum(CURRENCIES),
});

const componentFormSchema = zod.object({
  id: zod.string(),
  serviceId: zod.string(),
  servicePlanId: zod.string(),
  quantity: zod.string(),
  unitPrice: zod.string(),
  currency: zod.enum(CURRENCIES),
  contactId: zod.string(),
  expertId: zod.string(),
});

/**
 * A form-input validálása. A `mode` a create/edit ág (a scope/plan/billingStart kötelezőség create-kor él). A `t`
 * a séma-gyárba injektálva → az ÁRAZÓ blokk üzeneteit közvetlenül fordítja; a többi hiba-KÓD a
 * `buildServiceAssignmentErrorMap`-ben fordul a `validation:serviceAssignments.*` rétegre.
 */
export function buildServiceAssignmentSchema(mode: 'create' | 'edit', t: TFunction): z.ZodType {
  const v = (k: string): string => t(`validation:serviceAssignments.${k}`);
  return zod
    .object({
      scope: zod.enum(SERVICE_ASSIGNMENT_SCOPES),
      clientId: zod.string(),
      accountId: zod.string(),
      serviceId: zod.string(),
      servicePlanId: zod.string(),
      type: zod.enum(SERVICE_ASSIGNMENT_TYPES),
      status: zod.enum(SERVICE_ASSIGNMENT_STATUSES),
      quantity: decimalField.refine((val) => Number(val) >= 0.01),
      quantityUnit: zod.enum(INVOICE_QUANTITY_UNITS),
      issuerKey: zod.enum(INVOICE_ISSUER_KEYS),
      invoiceType: zod.enum(INVOICE_TYPES),
      invoiceDeliveryType: zod.enum(INVOICE_DELIVERY_TYPES),
      billingFrequency: zod.string(),
      billingAgreement: zod.string(),
      billingStartDate: zod.string(),
      billingEndDate: zod.string(),
      contractStartDate: zod.string(),
      contractEndDate: zod.string(),
      firstInvoiceProrated: zod.boolean(),
      note: zod.string().max(5000),
      pricing: pricingSchema,
      catalogPricingType: zod.string(),
      billingType: zod.string(),
      paymentCardId: zod.string(),
      contactId: zod.string(),
      expertId: zod.string(),
      accountIds: zod.array(zod.string()),
      invoiceItemName: zod.string().max(255),
      invoiceItemNote: zod.string().max(1000),
      showComponentsOnInvoice: zod.boolean(),
      usageSourceType: zod.string(),
      usageRefs: zod.array(zod.string()),
      splitSharedUsage: zod.boolean(),
      components: zod.array(componentFormSchema),
    })
    .superRefine((values, ctx) => {
      if (mode === 'create') {
        if (values.servicePlanId.trim() === '') {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['servicePlanId'] });
        }
        if (values.scope === 'account' && values.accountId.trim() === '') {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['accountId'] });
        }
        // Billing típusnál a számlázás kezdete kötelező (a szerver is elvárja a recurring-hoz).
        if (values.type === 'billing' && values.billingStartDate.trim() === '') {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['billingStartDate'] });
        }
      }
      // ESETI DÍJ: a „Számlázás dátuma" MINDKÉT módban kötelező (az egyetlen dátuma — enélkül a kimutatás egyetlen
      // hónapba sem sorolná; a szerver is 422-zi). Saját üzenet-kulccsal (`params.key`).
      if (values.billingType === 'one_time' && values.billingStartDate.trim() === '') {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['billingStartDate'],
          params: { key: 'billingDateRequired' },
        });
      }
      // ÉRINTETT FIÓKOK — ügyfél-hatókörnél KÖTELEZŐ, legalább egy (a legacy `required|array|min:1` 1:1).
      //
      // ⚠️ NEM FORMASÁG: a mező a SZÁMLÁZÁST vezérli. Használat-alapú (sávos/jutalékos) ügyfél-szintű sornál a
      // kijelölt fiókok döntik el, MELYIK hotelek mért forgalma adódik össze a tételbe — és az ÜRES pivot a
      // generálóban „az ügyfél ÖSSZES fiókja" jelentésű fallback (`usage-pricing.ts`). Üresen hagyva tehát a
      // számla csendben mindenre összegezne, miközben a szerződés nem azt mondja. A legacyben ez nem fordulhatott
      // elő: ott a mező kötelező volt ÉS előre ki volt töltve.
      //
      // A szabály MINDKÉT módban él (a legacy sem kötötte módhoz): egy meglévő, üres kijelölésű sor szerkesztése
      // így kikényszeríti a pótlást — pont azt a csendes esetet szüntetve meg, ami miatt a fal kell.
      if (values.scope === 'client' && values.accountIds.length === 0) {
        ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['accountIds'] });
      }
      // ÁR-OVERRIDE validáció CSAK billing típusnál (a mátrix/monotonitás; a KÖZÖS árazó-validátor).
      if (values.type === 'billing') {
        validatePricingForm(values.pricing, ctx, ['pricing'], v);
        // billingEndDate ≥ billingStartDate (a legacy 1:1) — ha mindkettő adott. A vég date-only ISO → lexiko-
        // grafikus összehasonlítás korrekt. A BFF a recurring-nuanszot is 422-zi; a UI billing-ága elég.
        const start = values.billingStartDate.trim();
        const end = values.billingEndDate.trim();
        if (start !== '' && end !== '' && end < start) {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['billingEndDate'] });
        }
      }
      // usageSourceType KÖTELEZŐ tiered/base_tiered/commission árazásnál (a legacy 1:1; a BFF is 422-zi). Az
      // EFFEKTÍV árazás-típus MINDKÉT típusnál az override — a nyilvántartás-sor árazása is a felhasználó
      // döntése, nem a katalógusé (korábban tracking-nél a katalógus-típus döntött, mert a sor nem kapott árat).
      const effPricingType = values.pricing.pricingType;
      if (usageSourceRequired(effPricingType) && values.usageSourceType.trim() === '') {
        ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['usageSourceType'] });
      }
      // A MÉRT AZONOSÍTÓK — használat-alapú tételnél legalább egy kell (a BFF is 422-zi): enélkül a tétel nem
      // tudná, melyik fiók melyik azonosítójának mérése adja az összegét.
      if (isUsageBasedForm(values) && values.usageRefs.length === 0) {
        ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['usageRefs'] });
      }
      // KOMPONENS-FA: soronként a plan kötelező + a nettó ár formátuma; a fa deviza-egységes legyen (a BFF is 422-zi).
      values.components.forEach((c, i) => {
        if (c.servicePlanId.trim() === '') {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['components', i, 'servicePlanId'] });
        }
        if (!/^\d+(\.\d{1,2})?$/.test(c.unitPrice.trim())) {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['components', i, 'unitPrice'] });
        }
      });
      if (
        values.components.length > 0 &&
        new Set(values.components.map((c) => c.currency)).size > 1
      ) {
        ctx.addIssue({ code: zod.ZodIssueCode.custom, path: ['components'] });
      }
    });
}

/** Lokalizált zod-hibatérkép: a custom üzenet-KULCSOKAT a `validation:serviceAssignments.*` rétegre fordítja. */
export function buildServiceAssignmentErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = typeof issue.path[0] === 'string' ? issue.path[0] : '';
    const msg = (k: string): { message: string } => ({
      message: t(`validation:serviceAssignments.${k}`),
    });
    switch (field) {
      case 'quantity':
        // regex-hiba (invalid_string) → formátum; a min-refine (custom) → minimum.
        return issue.code === zod.ZodIssueCode.invalid_string
          ? msg('quantityFormat')
          : msg('quantityMin');
      case 'servicePlanId':
        return msg('planRequired');
      case 'accountId':
        return msg('accountRequired');
      case 'accountIds':
        return msg('accountIdsRequired');
      case 'billingStartDate':
        return issue.code === zod.ZodIssueCode.custom &&
          (issue.params as { key?: string } | undefined)?.key === 'billingDateRequired'
          ? msg('billingDateRequired')
          : msg('billingStartRequired');
      case 'billingEndDate':
        return msg('billingEndBeforeStart');
      case 'usageSourceType':
        return msg('usageSourceRequired');
      case 'usageRefs':
        return msg('usageRefsRequired');
      case 'components':
        return msg('componentInvalid');
    }
    if (issue.code === zod.ZodIssueCode.too_big) return msg('tooLong');
    return { message: ctx.defaultError };
  };
}

// ── Felelős-választók: KAPCSOLATTARTÓ vs. SZAKÉRTŐ ─────────────────────────────────────────────────────
//
// A két mező KÉT KÜLÖN személy-halmazból választ, és ez üzleti szabály, nem UI-kényelem:
//   • **szakértő** = a cég BELSŐ munkatársa (`internal`) — ő viszi szakmailag a szolgáltatást,
//   • **kapcsolattartó** = az ügyfél-oldali KÜLSŐS (`partner`) — vele tartjuk a kapcsolatot.
// Adat-szinten a pivot mindkettőt elfogadja (nincs DB-kényszer), ezért a helyes kitöltés fala a UI-ban van.
// Egy közös, szűretlen listából a felhasználó bármikor a rossz oldalról választhatna — és semmi nem jelezné.
//
// A `service` típusú GÉPI fiókok EGYIK listában sem jelennek meg: nem emberek, nem lehetnek felelősök.
// Ismeretlen típus szintén kimarad (fail-safe: inkább ne ajánljuk fel, mint hogy tévesen belsősnek mutassuk).

/** Egy választható személy a refs-ből (a `name` a hívó nyelve szerinti sorrendben áll össze). */
export interface AssignmentIdentityRef {
  id: string;
  name: string;
  type: 'internal' | 'partner' | 'service';
}

/** Egy választó-opció (az `EntityCombobox` alakja). */
export interface AssignmentPersonOption {
  id: string;
  label: string;
}

/**
 * A refs személy-listájának szétválasztása a két felelős-mezőre. A bemeneti sorrendet MEGTARTJA (a szerver
 * már névsorba rendezte a látott néven), tehát itt nincs újrarendezés.
 */
export function splitAssignmentPeople(identities: readonly AssignmentIdentityRef[]): {
  /** Külsős (partner) személyek — a KAPCSOLATTARTÓ-választó. */
  contacts: AssignmentPersonOption[];
  /** Belső munkatársak — a SZAKÉRTŐ-választó. */
  experts: AssignmentPersonOption[];
} {
  const contacts: AssignmentPersonOption[] = [];
  const experts: AssignmentPersonOption[] = [];
  for (const i of identities) {
    const option = { id: i.id, label: i.name };
    if (i.type === 'partner') contacts.push(option);
    else if (i.type === 'internal') experts.push(option);
    // `service` (és bármi ismeretlen) → egyik listába sem
  }
  return { contacts, experts };
}

/**
 * A KAPCSOLATTARTÓ-választó halmaza a CÉL-ÜGYFÉLRE szűkítve.
 *
 * ══ MIÉRT ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * A választó korábban a TELJES partner-névtárat kínálta (mérve élesben: 486 ember), miközben egy fiókhoz
 * átlagosan 2 kapcsolattartó tartozik. Semmi nem jelezte, ha a felhasználó egy MÁSIK ügyfél emberét
 * választja — és ez nem elméleti: 343 hozzárendelés-kapcsolattartóból 12 olyan van, aki az adott ügyfél
 * egyetlen fiókjához sem kötődik.
 *
 * ══ A SZABÁLY (user-döntés) ════════════════════════════════════════════════════════════════════════════
 *
 * ÜGYFÉL-szint, nem fiók-szint: az ügyfél BÁRMELY fiókjának kapcsolattartója választható. Egy ember több
 * fióknál is lehet kapcsolattartó — a listában akkor is EGYSZER szerepel (a szűrés az identity-listán megy,
 * tehát a deduplikáció természetes).
 *
 * ══ KÉT FAIL-SAFE, ami nélkül rosszabb lenne, mint a hiba ══════════════════════════════════════════════
 *
 *  1. ÜRES kötés-lista → NEM szűkítünk. A kötések olvasása best-effort (jog-hiány/upstream-hiba → üres),
 *     és egy némán üres kapcsolattartó-választó rosszabb, mint egy tágabb: a felhasználó azt hinné, nincs
 *     kihez kötni a szolgáltatást.
 *  2. A MÁR BEÁLLÍTOTT értékek mindig benne maradnak, akkor is, ha nem felelnek meg a szűrőnek — különben a
 *     fenti 12 soron a szerkesztő olyan értéket mutatna, ami nincs a listában (pont az a hibaosztály, amit a
 *     select-ön-törlésnél javítottunk), és egy mentés CSENDBEN levenné a kapcsolattartót. A `megtartando`
 *     ezért a KOMPONENS-sorok kapcsolattartóit is tartalmazza, nem csak a szülőét — azok ugyanebből a
 *     listából választanak.
 */
export function contactsForClient(
  contacts: readonly AssignmentPersonOption[],
  affiliations: readonly { identityId: string; accountId: string }[],
  clientAccountIds: readonly string[],
  megtartando: readonly string[],
): AssignmentPersonOption[] {
  if (affiliations.length === 0) return [...contacts];
  const accounts = new Set(clientAccountIds);
  const engedett = new Set(
    affiliations.filter((a) => accounts.has(a.accountId)).map((a) => a.identityId),
  );
  const marad = new Set(megtartando.filter((id) => id !== ''));
  return contacts.filter((c) => engedett.has(c.id) || marad.has(c.id));
}
